import QueryKeys from '@api/index';
import { useProjectSettings } from '@api/settings';
import { ApprovalDialog } from '@components/Forms';
import { LoadingButton } from '@mui/lab';
import { Box, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC } from 'react';
import { ITheme } from 'types/theme';
import { IWizardProps, WizardPhase } from 'types/wizard';
import { useBoolean } from 'usehooks-ts';
import { WIZARD_PAGES } from '../utils/consts';
import { useWizardUpdate } from '../utils/hook';

const WizardGenerateButton: FC<IWizardProps> = (props) => {
	const [loading, setLoading] = React.useState(false);
	const { state } = props;
	const { data } = useProjectSettings();
	const { update, apply } = useWizardUpdate(state);
	const queryClient = useQueryClient();
	const { value: open, setTrue, setFalse } = useBoolean(false);
	const { palette } = useTheme<ITheme>();

	const handleClick = React.useCallback(async () => {
		setFalse();
		setLoading(true);

		const [applied] = await Promise.all([apply()]);

		if (applied) {
			await update({
				phase: WizardPhase.Integrate,
				page: WIZARD_PAGES.preview
			});

			queryClient.refetchQueries({ queryKey: [QueryKeys.flows] });
			queryClient.refetchQueries({ queryKey: [QueryKeys.screens] });
		}

		setLoading(false);
	}, [apply, queryClient, setFalse, update]);

	return (
		<>
			{open && (
				<ApprovalDialog
					maxWidth="sm"
					variant="contained"
					color="primary"
					onApprove={handleClick}
					onCancel={setFalse}
					title={intl.tr('wizard.phase.build.apply.flows.dialog.title')}
					question={intl.tr('wizard.phase.build.apply.flows.dialog.question')}
					approveText={intl.tr('button.yes')}
					cancelText={intl.tr('button.cancel')}
				/>
			)}
			<LoadingButton
				data-testid="wizard-generate-button"
				variant="contained"
				onClick={data?.hasNoActivity ? handleClick : setTrue}
				loading={loading}
				loadingPosition="start"
				startIcon={<Box />}
				sx={{
					borderRadius: 44,
					paddingX: 6,
					background: !loading
						? palette.brand.descopeGradient(palette, 90, 0, undefined, 100)
						: 'disabled'
				}}
			>
				<Typography
					variant="body1"
					color="primary.contrast"
					whiteSpace="nowrap"
					textTransform="uppercase"
				>
					{loading
						? intl.tr('wizard.phase.generating.button')
						: intl.tr('wizard.phase.next.button')}
				</Typography>
			</LoadingButton>
		</>
	);
};

export default WizardGenerateButton;
